import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BaseModal } from '../components/common/UI/BaseModal';
import ProcurementActionStepper from '../components/Dashboard/ProcurementActionStepper';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useProcurements } from '../hooks/useProcurements';
import {
  archiveProcurement,
  createProcurement,
  deleteProcurement,
  uploadRFPForProcurement,
} from '../services/procurementService';
import storageService from '../services/storageService';
import { PERMISSIONS } from '../shared/constants/permissions';

const ProcurementsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();
  const { procurements, loading, error } = useProcurements();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Archive & Delete state
  const [procurementToArchive, setProcurementToArchive] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [procurementToDelete, setProcurementToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (location.state?.openCreate) {
      setOpenCreateDialog(true);
      // Clear the state so it doesn't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleOpenCreate = () => {
    setSelectedFiles([]);
    setOpenCreateDialog(true);
  };

  const handleCloseCreate = () => {
    setOpenCreateDialog(false);
  };

  const handleCreateSubmit = async () => {
    if (!account?.clientId || !account?.uid) {
      return;
    }

    setIsCreating(true);
    try {
      const documentPaths: string[] = [];
      const rfpDocuments = [];
      const procurementId = crypto.randomUUID(); // Pre-generate ID to use for storage path

      if (selectedFiles.length > 0) {
        showSnackbar('Uploading RFP documents...', 'info');
        for (const file of selectedFiles) {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const storagePath = `clients/${account.clientId}/teams/${account.teamId}/users/${account.uid}/procurements/${procurementId}/rfp_docs/${safeName}`;
          await storageService.uploadFile(storagePath, file);
          documentPaths.push(storagePath);
          rfpDocuments.push({
            id: crypto.randomUUID(),
            name: file.name,
            url: storagePath,
            uploadedAt: new Date().toISOString(),
          });
        }
      }

      await createProcurement({
        id: procurementId,
        clientId: account.clientId,
        teamId: account.teamId,
        title: selectedFiles.length > 0 ? 'Extracting from Documents...' : 'Untitled Procurement',
        description: selectedFiles.length > 0 ? 'Extracting from Documents...' : '',
        status: 'DRAFT',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // +30 days default
        ownerId: account.uid,
        rfpDocuments,
      });

      if (selectedFiles.length > 0) {
        await uploadRFPForProcurement(procurementId, account.clientId, documentPaths);
        showSnackbar('RFP uploaded. AI extraction started.', 'success');
      }

      handleCloseCreate();

      // Navigate to detail page.
      navigate(`/procure/detail/${procurementId}`);
    } catch (err: unknown) {
      showSnackbar((err as Error).message, 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchive = async () => {
    if (!procurementToArchive) {
      return;
    }
    setIsArchiving(true);
    try {
      await archiveProcurement(procurementToArchive.id);
      showSnackbar(`Archived ${procurementToArchive.title}`, 'success');
      setProcurementToArchive(null);
    } catch (err: unknown) {
      showSnackbar((err as Error).message, 'error');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!procurementToDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteProcurement(procurementToDelete.id);
      showSnackbar(`Deleted ${procurementToDelete.title}`, 'success');
      setProcurementToDelete(null);
      setDeleteConfirmText('');
    } catch (err: unknown) {
      showSnackbar((err as Error).message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'info' | 'warning' | 'success' => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'PUBLISHED':
        return 'info';
      case 'EVALUATION':
        return 'warning';
      case 'AWARDED':
        return 'success';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatStatusLabel = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'Draft';
      case 'PUBLISHED':
        return 'Published (Open)';
      case 'EVALUATION':
        return 'In Evaluation';
      case 'AWARDED':
        return 'Awarded';
      case 'ARCHIVED':
        return 'Archived';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading procurements: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid="dashboard">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Procurements Dashboard</Typography>
        {hasPermission(PERMISSIONS.TENDER_CREATE) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Create New Procurement
          </Button>
        )}
      </Box>

      {/* Instructional Section */}
      <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
        <AlertTitle sx={{ fontWeight: 'bold' }}>Welcome to the Procurement Lifecycle</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This platform guides you through the buying process. We use AI to automate the heavy
          lifting, ensure compliance, and help you prove Value-for-Money. The process has three main
          phases:
        </Typography>
        <Stepper alternativeLabel sx={{ mt: 2, mb: 1 }}>
          <Step active>
            <StepLabel icon={<DescriptionIcon color="primary" />}>
              <Typography variant="subtitle2" fontWeight="bold">
                1. Upload & Extract
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Upload your RFP documents and let AI automatically extract details and create a
                scoring rubric.
              </Typography>
            </StepLabel>
          </Step>
          <Step>
            <StepLabel icon={<AssessmentIcon color="action" />}>
              <Typography variant="subtitle2" fontWeight="bold">
                2. Compliance & Triage
              </Typography>
              <Typography variant="caption" color="textSecondary">
                The AI scans supplier submissions to make sure all mandatory requirements are met.
              </Typography>
            </StepLabel>
          </Step>
          <Step>
            <StepLabel icon={<GavelIcon color="action" />}>
              <Typography variant="subtitle2" fontWeight="bold">
                3. Evaluate & Award
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Your team scores the bids, moderates the results, and generates a final evaluation
                report.
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      </Alert>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Active Procurements
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {procurements.length === 0 ? (
          <Paper
            sx={{
              textAlign: 'center',
              mt: 3,
              p: 4,
              background: theme.palette.greyLight,
              color: theme.palette.text.primary,
              borderRadius: theme.spacing(1),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {hasPermission(PERMISSIONS.TENDER_CREATE) ? (
              <>
                <Typography variant="h6">Ready to create a new procurement?</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenCreate}
                    sx={{
                      backgroundColor: theme.palette.bidlogiq.brick,
                      color: `${theme.palette.common.white} !important`,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: theme.palette.bidlogiq.brick,
                        boxShadow: theme.shadows[4],
                      },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      border: 'none',
                      borderRadius: theme.spacing(1),
                      boxShadow: theme.shadows[2],
                    }}
                  >
                    + New Procurement
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="h6">
                You do not have any procurements assigned to you yet.
              </Typography>
            )}
          </Paper>
        ) : (
          procurements
            .filter((proc) => proc.status !== 'ARCHIVED')
            .map((proc) => {
              const statusColor = getStatusColor(proc.status);
              const colorMain =
                statusColor === 'default'
                  ? theme.palette.grey[400]
                  : theme.palette[statusColor].main;

              return (
                <Card
                  key={proc.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: `6px solid ${colorMain}`,
                    boxShadow: 2,
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {proc.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {proc.extractionStatus === 'PROCESSING' && (
                          <Chip
                            icon={<CircularProgress size={16} />}
                            label="Extracting Details..."
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        {proc.extractionStatus === 'ERROR' && (
                          <Chip
                            label="Extraction Failed"
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        <Chip
                          label={formatStatusLabel(proc.status)}
                          color={statusColor}
                          sx={{ fontWeight: 'bold', letterSpacing: '0.05em' }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                      {proc.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Due:</strong> {new Date(proc.dueDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>

                  <Divider />

                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, maxWidth: '60%' }}>
                      <ProcurementActionStepper procurement={proc} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="medium"
                        onClick={() =>
                          setProcurementToArchive({ id: proc.id, title: proc.title || proc.id })
                        }
                      >
                        Archive
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(`/procure/detail/${proc.id}`)}
                        sx={{
                          backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: theme.palette.bidlogiq?.brick
                              ? '#a03520'
                              : 'primary.dark',
                          },
                        }}
                      >
                        Open Procurement Toolkit
                      </Button>
                      <IconButton
                        onClick={() =>
                          setProcurementToDelete({ id: proc.id, title: proc.title || proc.id })
                        }
                        color="error"
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              );
            })
        )}
      </Box>

      {/* Create Procurement Dialog */}
      <BaseModal
        open={openCreateDialog}
        onClose={handleCloseCreate}
        maxWidth="sm"
        fullWidth
        title="New Procurement Event"
        actions={
          <>
            <Button onClick={handleCloseCreate} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={isCreating}
              sx={{ minWidth: 180 }}
            >
              {isCreating ? <CircularProgress size={24} color="inherit" /> : 'Create Procurement'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Upload RFP Documents (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your RFP, RFT, or EOI documents. Our AI will automatically extract the
              procurement details, scope of work, and evaluation criteria for you.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ py: 2, borderStyle: 'dashed' }}
            >
              Select Files
              <input
                type="file"
                multiple
                hidden
                accept=".pdf,.docx,.pptx,.txt"
                onChange={(e) => {
                  if (e.target.files) {
                    setSelectedFiles(Array.from(e.target.files));
                  }
                }}
              />
            </Button>
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedFiles.map((file) => (
                  <Typography
                    key={file.name}
                    variant="body2"
                    noWrap
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    • {file.name}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </BaseModal>

      {/* Archive Modal */}
      <BaseModal
        open={!!procurementToArchive}
        onClose={() => setProcurementToArchive(null)}
        title="Archive Procurement"
        maxWidth="xs"
        fullWidth
        actions={
          <>
            <Button onClick={() => setProcurementToArchive(null)} disabled={isArchiving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleArchive} disabled={isArchiving}>
              Archive
            </Button>
          </>
        }
      >
        <Typography variant="body1">
          Are you sure you want to archive <strong>{procurementToArchive?.title}</strong>? It will
          no longer appear on your dashboard.
        </Typography>
      </BaseModal>

      {/* Delete Modal */}
      <BaseModal
        open={!!procurementToDelete}
        onClose={() => {
          setProcurementToDelete(null);
          setDeleteConfirmText('');
        }}
        title="Delete Procurement"
        maxWidth="xs"
        fullWidth
        actions={
          <>
            <Button
              onClick={() => {
                setProcurementToDelete(null);
                setDeleteConfirmText('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmText !== 'DELETE'}
            >
              Delete Forever
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="error">
            <AlertTitle>Warning</AlertTitle>
            This action is final and unrecoverable. All associated suppliers, evaluations, and
            rubrics will also be deleted.
          </Alert>
          <Typography variant="body1">
            To confirm deletion of <strong>{procurementToDelete?.title}</strong>, please type{' '}
            <strong>DELETE</strong> below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDeleteConfirmText(e.target.value)
            }
            placeholder="DELETE"
          />
        </Box>
      </BaseModal>
    </Box>
  );
};

export default ProcurementsDashboard;
